import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from 'firebase/auth'
import { auth, loginWithEmail, loginWithGoogle, logout as firebaseLogout } from '@/lib/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { adminApi } from '@/lib/api'

interface AdminUser extends User {
  isAdmin: boolean
  role: string
  permissions: string[]
  backendUser?: any
}

interface AuthContextType {
  user: AdminUser | null
  isLoading: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  logout: () => Promise<void>
  refreshAdminStatus: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const validateAdminStatus = async (firebaseUser: User, forceTokenRefresh = false): Promise<AdminUser | null> => {
    try {
      console.log('🔍 Validating admin status for user:', firebaseUser.email)
      
      // Force token refresh if requested
      if (forceTokenRefresh) {
        console.log('🔄 Forcing token refresh before validation...')
        await firebaseUser.getIdToken(true)
      }
      
      // Check admin status via backend API
      console.log('🌐 Calling backend API: /api/users/me')
      const adminStatus = await adminApi.checkAdminStatus()
      console.log('🔍 Backend response:', adminStatus)
      
      if (!adminStatus.isAdmin) {
        console.warn('User is not an admin:', firebaseUser.email)
        throw new Error('Access denied: Admin privileges required')
      }

      // Get full user profile
      const backendUser = await adminApi.getCurrentUser()
      
      const adminUser: AdminUser = {
        ...firebaseUser,
        isAdmin: adminStatus.isAdmin,
        role: adminStatus.role,
        permissions: adminStatus.permissions,
        backendUser
      }

      console.log('Admin validation successful:', {
        email: firebaseUser.email,
        role: adminStatus.role,
        isAdmin: adminStatus.isAdmin
      })

      return adminUser
    } catch (error) {
      console.error('Admin validation failed:', error)
      
      // If it's an access denied error and we haven't tried token refresh yet, try once
      if (!forceTokenRefresh && (error as any).message?.includes('Access denied')) {
        console.log('Access denied - attempting token refresh and retry...')
        try {
          return await validateAdminStatus(firebaseUser, true)
        } catch (retryError) {
          console.error('Retry after token refresh also failed:', retryError)
        }
      }
      
      // Sign out user if they don't have admin access
      await firebaseLogout()
      throw error
    }
  }

  const refreshAdminStatus = async () => {
    if (!auth.currentUser) return
    
    try {
      const adminUser = await validateAdminStatus(auth.currentUser)
      setUser(adminUser)
    } catch (error) {
      setUser(null)
      throw error
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('🔍 Auth state changed:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user')
      setIsLoading(true)
      
      if (firebaseUser) {
        try {
          console.log('🔍 Validating admin status for:', firebaseUser.email)
          const adminUser = await validateAdminStatus(firebaseUser)
          console.log('✅ Admin validation successful:', adminUser)
          setUser(adminUser)
        } catch (error) {
          console.error('❌ Authentication failed:', error)
          setUser(null)
        }
      } else {
        console.log('ℹ️ No user, clearing state')
        setUser(null)
      }
      
      setIsLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const userCredential = await loginWithEmail(email, password)
      
      // Wait for auth state change to process
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Login timeout'))
        }, 5000)
        
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser && firebaseUser.uid === userCredential.user.uid) {
            clearTimeout(timeout)
            unsubscribe()
            
            try {
              const adminUser = await validateAdminStatus(firebaseUser)
              if (!adminUser?.isAdmin) {
                await firebaseLogout()
                reject(new Error('Access denied: Admin privileges required'))
              } else {
                resolve()
              }
            } catch (error) {
              reject(error)
            }
          }
        })
      })
    } catch (error) {
      setIsLoading(false)
      throw error
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    try {
      console.log('🔄 handleGoogleLogin called')
      const result = await loginWithGoogle()
      console.log('🔍 Login result:', result)
      // Validation will happen via onAuthStateChanged
    } catch (error) {
      console.error('❌ Google login failed:', error)
      setIsLoading(false)
      throw error
    }
  }

  const logout = async () => {
    await firebaseLogout()
    setUser(null)
  }

  const value = {
    user,
    isLoading,
    isAdmin: user?.isAdmin || false,
    login,
    loginWithGoogle: handleGoogleLogin,
    logout,
    refreshAdminStatus
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}